import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ModalComponentBaseProps,
  useModal,
  useToast,
} from '@pcmp/react-providers';
import { ModalHeader, Sheets, ModalBody } from '@ingka/modal';
import Button from '@ingka/button';
import {
  useContentTracking,
  useInteractionTracking,
} from '@/lib/hooks/tracking';
import ControlledInput from '@/components/forms/controlled-input';
import { ContactFormData, contactSchema } from '@/lib/schemas/contact';
import useYupValidationResolver from '@/lib/hooks/yup';
import ControlledText from '@/components/forms/controlled-text';
import { useUser } from '@/redux/features/session/hooks';
import { useSendMessageMutation } from '@/redux/back-dashboard-api';
import { ErrorToast } from '@/components/helpers/toast-messages';

const ContactSider: FC<ModalComponentBaseProps> = ({
  handleDismiss,
  ...rest
}) => {
  // Hooks
  const trackContent = useContentTracking();
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  const trackInteraction = useInteractionTracking();
  const [isLoading, setIsLoading] = useState(false);
  const [sendMessage] = useSendMessageMutation();
  const { pushToast } = useToast();

  // Data
  const currUser = useUser();
  const resolver = useYupValidationResolver(contactSchema);
  const form = useForm<ContactFormData>({
    mode: 'all',
    resolver,
    defaultValues: {
      email: currUser?.email || '',
      message: '',
    },
    criteriaMode: 'all',
  });
  const submitIsEnabled = form.formState.isValid && !isLoading;
  const confirm = async (contactMessage: {
    email: string;
    message: string;
  }) => {
    const response = await sendMessage({ sendMessageInput: contactMessage });

    const isError =
      response && ('error' in response || response.data?.sendMessage === false);
    const toastContent = isError ? (
      <ErrorToast error={t('page.contact.result.error')} />
    ) : (
      t('page.contact.result.success')
    );
    pushToast({ message: toastContent });

    //  This prevents closing the modal there was an error
    if (!isError) {
      form.reset();
    } else {
      console.error(response.error);
    }
  };

  useEffect(() => {
    trackContent('contact', { type: 'sider' });
  }, []);

  // Event handlers
  const { handleSubmit } = form;
  const handleConfirm = handleSubmit(async (data) => {
    setIsLoading(true);
    try {
      await confirm(data);
      handleDismiss();
      trackInteraction('contact-submit', { content: 'contact' });
    } finally {
      setIsLoading(false);
    }
  });

  const goBack = async () => {
    await closeModal(0);
    const { default: HelpCenterSider } = await import('..');

    await openModal({
      component: HelpCenterSider,
      props: {},
    });
  };

  return (
    <Sheets
      {...rest}
      data-testid="contactModal"
      aria-label={t('page.contact.description')}
      footer={null}
      header={
        <ModalHeader
          ariaCloseTxt={t('aria.close')}
          title={t('page.contact.title')}
          backBtnClick={goBack}
        />
      }
    >
      <ModalBody>
        <FormProvider {...form}>
          <p className="mb-4">{t('page.contact.intro')}</p>
          <ControlledInput<ContactFormData>
            name="email"
            label={t('label.email')}
            type="text"
          />
          <ControlledText<ContactFormData>
            name="message"
            label={t('label.message')}
            shouldValidate={true}
          />
          <p>
            <small>{t('page.contact.footnote')}</small>
          </p>

          <Button
            data-testid="submitContactBtn"
            disabled={!submitIsEnabled}
            text={t('page.contact.confirmText')}
            type="primary"
            className="w-full mt-3"
            onClick={handleConfirm}
          />
        </FormProvider>
      </ModalBody>
    </Sheets>
  );
};

export default ContactSider;
